import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { ReportGeneratingSkeleton } from './ReportSkeletons';

const STEPS = [
  'Analyzing emirate listing data...',
  'Computing median price movements...',
  'Evaluating YoY growth trends...',
  'Reviewing RERA/DLD regulatory updates...',
  'Assessing off-plan market launches...',
  'Analyzing expat buying patterns...',
  'Compiling investment hotspot rankings...',
  'Generating executive summary...',
];

const GenerationStatus = ({ isGenerating, emirate, period }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isGenerating) {
      setCurrentStep(0);
      setElapsed(0);
      return;
    }
    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => (prev < STEPS?.length - 1 ? prev + 1 : prev));
    }, 2200);
    const timeInterval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => {
      clearInterval(stepInterval);
      clearInterval(timeInterval);
    };
  }, [isGenerating]);

  if (!isGenerating) return null;

  const progress = Math.round(((currentStep + 1) / STEPS?.length) * 100);
  const estimatedTotal = Math.round(STEPS?.length * 2.2);
  const remaining = Math.max(0, estimatedTotal - elapsed);

  return (
    <div>
      {/* Progress Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-blue-900 text-sm">OpenAI is generating your report</p>
            <p className="text-blue-600 text-xs truncate">{emirate === 'all' ? 'All Emirates' : emirate} · {period} · Est. {remaining}s remaining</p>
          </div>
          <div className="ml-auto text-blue-700 font-bold text-lg tabular-nums">{progress}%</div>
        </div>

        {/* Animated progress bar */}
        <div className="w-full bg-blue-200 rounded-full h-2.5 mb-3 overflow-hidden">
          <div
            className="h-2.5 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #2563eb, #3b82f6, #60a5fa)' }}
          >
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite linear',
              }}
            />
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1">
          {STEPS?.map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 h-1.5 rounded-full transition-all duration-500"
              style={{
                width: i <= currentStep ? '20px' : '8px',
                background: i < currentStep ? '#2563eb' : i === currentStep ? '#3b82f6' : '#bfdbfe',
              }}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 text-blue-700 text-xs">
          <Icon name="Loader" size={12} className="animate-spin" />
          <span className="font-medium">{STEPS?.[currentStep]}</span>
        </div>
      </div>

      {/* Skeleton placeholders for report content */}
      <ReportGeneratingSkeleton />
    </div>
  );
};

export default GenerationStatus;
